import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '../ui/button'
import { useToast } from '../../hooks/use-toast'
import { Alert, AlertDescription } from '../ui/alert'
import { supabase } from '../../lib/supabase'
import { Session } from '@supabase/supabase-js'
import { useAuth } from '../../lib/auth.context'
import { InputOTP } from '../input-otp'

interface TOTPVerifyProps {
  onComplete?: () => void
  redirectTo?: string
}

export function TOTPVerify({ onComplete, redirectTo }: TOTPVerifyProps) {
  const [verifyCode, setVerifyCode] = useState('')
  const [formError, setFormError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [factorId, setFactorId] = useState<string>('')
  const navigate = useNavigate()
  const { toast } = useToast()
  const { setIsTOTPVerified } = useAuth()

  useEffect(() => {
    const getTOTPFactor = async () => {
      try {
        // Wait longer to ensure session is properly established
        await new Promise(resolve => setTimeout(resolve, 500))

        // Try multiple times to get the session if it's not immediately available
        let currentSession = null
        let attempts = 0
        const maxAttempts = 5

        while (!currentSession && attempts < maxAttempts) {
          const { data: { session } } = await supabase.auth.getSession()
          currentSession = session

          if (!currentSession) {
            attempts++
            if (attempts < maxAttempts) {
              console.log(`[TOTPVerify] No session found, attempt ${attempts}/${maxAttempts}, retrying in 200ms...`)
              await new Promise(resolve => setTimeout(resolve, 200))
            }
          }
        }

        if (!currentSession) {
          throw new Error('No active session found. Please log in again.')
        }

        console.log('[TOTPVerify] Session verified, getting TOTP factors')
        const { data: factors } = await supabase.auth.mfa.listFactors()
        if (!factors?.totp?.length) throw new Error('No TOTP factors found')

        // Find the first verified TOTP factor
        const verifiedTotpFactor = factors.totp.find(factor => factor.status === 'verified')
        if (!verifiedTotpFactor) throw new Error('No verified TOTP factor found')

        console.log('[TOTPVerify] verified totpFactor:', verifiedTotpFactor)
        setFactorId(verifiedTotpFactor.id)
      } catch (error: any) {
        const errorMessage = error.message || 'Failed to get TOTP factor'
        console.error('[TOTPVerify] Error getting TOTP factor:', error)
        setFormError(errorMessage)
        toast({
          title: 'Error',
          description: errorMessage,
          variant: 'destructive',
        })
      }
    }

    getTOTPFactor()
  }, [toast])

  const handleVerify = async (e?: React.FormEvent) => {
    e?.preventDefault()
    setFormError('')
    setIsLoading(true)

    try {
      if (!factorId) throw new Error('TOTP factor not found')

      // Verify we still have a valid session
      const { data: { session: existingSession } } = await supabase.auth.getSession()
      if (!existingSession) {
        throw new Error('Session expired. Please log in again.')
      }

      console.log('[TOTPVerify] factorId:', factorId, 'session user:', existingSession.user?.id)
      const { data: challenge, error } = await supabase.auth.mfa.challenge({
        factorId: factorId
      })

      if (error) throw error

      const { data, error: verifyError } = await supabase.auth.mfa.verify({
        factorId: factorId,
        challengeId: challenge.id,
        code: verifyCode
      })

      if (verifyError) throw verifyError

      // Create a new session object from the response
      const newSession: Session = {
        access_token: data.access_token,
        refresh_token: data.refresh_token,
        expires_in: data.expires_in,
        expires_at: Math.floor(Date.now() / 1000) + data.expires_in,
        token_type: data.token_type,
        user: data.user
      }

      // Set the new session with aal2 token
      await supabase.auth.setSession(newSession)

      // Update TOTP verification status in auth context
      console.log('[TOTPVerify] Setting TOTP verification to true after successful verification')
      setIsTOTPVerified(true)

      toast({
        title: 'Success',
        description: 'TOTP verification successful',
      })

      // When the caller supplies onComplete it owns the post-verification flow
      // (e.g. the reset-password page still has a password to submit).
      if (onComplete) {
        onComplete()
        return
      }

      // FF-3636: the wrong-portal redirect used to live here. It has moved to auth.context, which
      // runs on every auth state change rather than only on this one login path, and which signs
      // the user out before redirecting instead of leaving the session persisted.
      if (redirectTo?.startsWith('http')) {
        // Handle any other external URLs
        window.location.replace(redirectTo)
      } else if (redirectTo) {
        // Internal route navigation
        navigate(redirectTo)
      } else {
        // Default internal route
        navigate('/admin/dashboard')
      }
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to verify TOTP'
      setFormError(errorMessage)
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="w-full space-y-8">
      <div className="space-y-2">
        <h1 className="text-2xl font-medium text-white">Enter the 6-digit code from your authentication app</h1>
        <p className="text-slate-400 font-light">(Google Authenticator, Microsoft Authenticator, etc).</p>
      </div>
      <form onSubmit={handleVerify}>
        <div className="flex flex-col items-center gap-8 w-full">
          {formError && (
            <Alert variant="destructive">
              <AlertDescription className="text-white">{formError}</AlertDescription>
            </Alert>
          )}
          <InputOTP length={6} onChange={(value) => setVerifyCode(value)} />
          <Button
            type="submit"
            disabled={isLoading || !verifyCode}
            className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
          >
            {isLoading ? 'Verifying...' : 'Verify Code'}
          </Button>
        </div>
      </form>
    </div>
  )
}
